import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useBranding } from '../../contexts/BrandingContext';
import { homeDoPerfil } from '../../components/common/PrivateRoute';
import { login, selecionarEmpresa } from '../../api/auth';

export default function Login() {
  const { loginSuccess } = useAuth();
  const { branding, loadBySlug, loadAtiva, resetToDefault } = useBranding();
  const navigate = useNavigate();
  const { slug } = useParams();
  const [form, setForm] = useState({ email: '', senha: '' });
  const [verSenha, setVerSenha] = useState(false);
  const [manter, setManter] = useState(true);
  const [erro, setErro] = useState('');
  const [loading, setLoading] = useState(false);

  // Preenchido quando o backend responde requer_selecao_empresa=true (colab) — usuário
  // vinculado a mais de uma empresa precisa escolher qual entrar.
  const [selecao, setSelecao] = useState(null); // { id_usuario_portal, empresas }

  useEffect(() => {
    // Login SEM slug (/login) é a porta genérica da plataforma — nunca deve expor
    // nome/logo de uma empresa específica, mesmo que outra já tenha sido carregada
    // antes nesta mesma aba (sessão anterior de outro usuário/empresa). Só quando
    // há slug explícito (/e/:slug/login) é que mostramos a marca daquela empresa.
    if (slug) loadBySlug(slug);
    else resetToDefault();
  }, [slug, loadBySlug, resetToDefault]);

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const entrarComResposta = async (data) => {
    loginSuccess(data);
    await loadAtiva();
    navigate(homeDoPerfil(data.usuario?.perfil), { replace: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErro('');
    setLoading(true);
    try {
      const { data } = await login(form.email, form.senha);
      if (data.requer_selecao_empresa) {
        setSelecao({ id_usuario_portal: data.id_usuario_portal, empresas: data.empresas });
        return;
      }
      await entrarComResposta(data);
    } catch (err) {
      const msg = err.response?.data?.detail;
      setErro(typeof msg === 'string' ? msg : 'Email ou senha inválidos.');
    } finally {
      setLoading(false);
    }
  };

  const handleEscolherEmpresa = async (idEmpresa) => {
    setErro('');
    setLoading(true);
    try {
      const { data } = await selecionarEmpresa(selecao.id_usuario_portal, idEmpresa, form.senha);
      await entrarComResposta(data);
    } catch (err) {
      const msg = err.response?.data?.detail;
      setErro(typeof msg === 'string' ? msg : 'Erro ao selecionar empresa.');
      setSelecao(null);
    } finally {
      setLoading(false);
    }
  };

  // Coluna de marca — compartilhada pelos dois estados da tela
  const aside = (
    <aside className="cjc-login-aside">
      <div className="cjc-login-brand">
        {branding?.logo_url
          ? <img src={branding.logo_url} alt={branding.nome} />
          : <>{branding?.nome || 'DataFit'} <span>B2B</span></>}
      </div>

      <div className="cjc-login-pitch">
        <div className="cjc-login-eyebrow">
          Portal de pedidos
          <i />
        </div>
        <h1 className="cjc-login-headline">
          Pedir<br />é<br /><em>rápido.</em>
        </h1>
        <p className="cjc-login-sub">
          Catálogo, estoque e acompanhamento de produção no mesmo lugar.
          Entre e retome de onde parou.
        </p>
      </div>

      <div className="cjc-login-foot">Acesso restrito · {branding?.nome || 'DataFit'}</div>
    </aside>
  );

  if (selecao) {
    return (
      <div className="cjc-login-page">
        <div className="cjc-login-grid">
          {aside}
          <div className="cjc-login-col">
            <div className="cjc-login-card">
              <h2>Selecione a empresa</h2>
              <p className="cjc-login-hint">Seu usuário está vinculado a mais de uma empresa.</p>

              {erro && <div className="alert py-2">{erro}</div>}

              <div className="cjc-login-empresas">
                {selecao.empresas.map(emp => (
                  <button
                    key={emp.id_empresa_operadora}
                    type="button"
                    className="cjc-login-empresa"
                    disabled={loading}
                    onClick={() => handleEscolherEmpresa(emp.id_empresa_operadora)}
                  >
                    {emp.nome}
                  </button>
                ))}
              </div>

              <button type="button" className="cjc-login-back" onClick={() => setSelecao(null)}>
                ← Voltar
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cjc-login-page">
      <div className="cjc-login-grid">
        {aside}

        <div className="cjc-login-col">
          <div className="cjc-login-card">
            <h2>Acessar</h2>
            <p className="cjc-login-hint">Credenciais fornecidas pela sua empresa.</p>

            {erro && <div className="alert py-2">{erro}</div>}

            <form className="cjc-login-form" onSubmit={handleSubmit}>
              <div className="cjc-login-field">
                <label className="cjc-login-label" htmlFor="login-email">E-mail</label>
                <input
                  id="login-email"
                  type="email"
                  name="email"
                  className="form-control"
                  placeholder="nome@empresa.com.br"
                  value={form.email}
                  onChange={handleChange}
                  required
                  autoFocus
                  autoComplete="email"
                />
              </div>

              <div className="cjc-login-field">
                <div className="cjc-login-field-top">
                  <label className="cjc-login-label" htmlFor="login-senha">Senha</label>
                  <button
                    type="button"
                    className="cjc-login-toggle"
                    onClick={() => setVerSenha(v => !v)}
                  >
                    {verSenha ? 'Ocultar' : 'Mostrar'}
                  </button>
                </div>
                <input
                  id="login-senha"
                  type={verSenha ? 'text' : 'password'}
                  name="senha"
                  className="form-control"
                  placeholder="••••••••"
                  value={form.senha}
                  onChange={handleChange}
                  required
                  autoComplete="current-password"
                />
              </div>

              <div className="cjc-login-row">
                <label className="cjc-login-check">
                  <input
                    type="checkbox"
                    checked={manter}
                    onChange={e => setManter(e.target.checked)}
                  />
                  Manter-me conectado
                </label>
                <a className="cjc-login-link" href="/recuperar-senha">Esqueci a senha</a>
              </div>

              <button type="submit" className="cjc-login-submit" disabled={loading}>
                <span>{loading ? 'Entrando' : 'Entrar'}</span>
                <span aria-hidden="true" style={{ fontSize: 16, lineHeight: 1 }}>→</span>
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
